import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics configuration
const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID || 'G-XXXXXXXXXX';

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window !== 'undefined' && GA_TRACKING_ID && GA_TRACKING_ID !== 'G-XXXXXXXXXX') {
    // Check if GA is already initialized
    if (window.dataLayer && window.dataLayer.length > 0) {
      console.log('Google Analytics already initialized');
      return;
    }

    // Load Google Analytics script FIRST
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    script1.onload = () => {
      console.log('Google Analytics script loaded successfully');
      
      // Initialize dataLayer and gtag function AFTER script loads
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]) {
        window.dataLayer.push(args);
        console.log('gtag called:', args);
      }
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', GA_TRACKING_ID, {
        page_title: document.title,
        page_location: window.location.href,
        send_page_view: true,
      });
      
      console.log('GA config sent:', GA_TRACKING_ID, window.location.href);
    };
    script1.onerror = () => {
      console.error('Failed to load Google Analytics script');
    };
    document.head.appendChild(script1);
  } else {
    console.warn('Google Analytics not initialized: Invalid tracking ID or window not available');
  }
};

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
      page_title: title || document.title,
    });
  }
};

// Track custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track e-commerce events
export const trackPurchase = (transactionId: string, value: number, currency: string, items: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items,
    });
  }
};

// Track workshop registration
export const trackWorkshopRegistration = (workshopTitle: string, workshopId: string, price: number) => {
  trackEvent('workshop_registration', 'engagement', workshopTitle, price);
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-CONVERSION_ID/CONVERSION_LABEL',
      value: price,
      currency: 'ILS',
      workshop_id: workshopId,
    });
  }
};

// Track tutorial purchase
export const trackTutorialPurchase = (tutorialTitle: string, tutorialId: string, price: number) => {
  trackEvent('tutorial_purchase', 'ecommerce', tutorialTitle, price);
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-CONVERSION_ID/CONVERSION_LABEL',
      value: price,
      currency: 'ILS',
      tutorial_id: tutorialId,
    });
  }
};

// Track search queries
export const trackSearch = (searchTerm: string, resultsCount: number) => {
  trackEvent('search', 'engagement', searchTerm, resultsCount);
};

// Track language change
export const trackLanguageChange = (fromLang: string, toLang: string) => {
  trackEvent('language_change', 'engagement', `${fromLang}_to_${toLang}`);
};

// Google Analytics component
export const GoogleAnalytics: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views on route changes (main GA is loaded in HTML)
    if (typeof window !== 'undefined' && window.dataLayer) {
      trackPageView(location.pathname + location.search);
      
      // Debug: Log page view tracking
      if (process.env.NODE_ENV === 'development') {
        console.log('GA Page view tracked:', location.pathname + location.search);
      }
    }
  }, [location]);

  return null;
};

// Declare global gtag function
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
